export type OperationsPermissions = {
    viewingClientPhoneNumber?: boolean;
    viewingScheduleAllMasters?: boolean;
    createRecord?: boolean;
    transferringRecord?: boolean;
    settingScheduleAll?: boolean;
};

export type Cancellation = {
    recordId: number;
    clientId: number;
    clientName: string;
    clientPhone: string | null;
    staffId: number | null;
    staffName: string | null;
    cancelledAt: string;
    dateTime: string | null;
    services: { id: number; title: string; minutes: number }[];
};

export type Operation = {
    id: string;
    type: "gap" | "shift" | "cancellation" | "support";
    staffId?: number;
    staffName?: string;
    start?: string;
    end?: string;
    shiftStart?: string;
    minutes?: number;
    riskAmount: number | null;
    serviceIds?: number[];
    records?: { recordId: number; start: string; end: string }[];
    candidate?: Cancellation;
    ticketId?: number;
    title?: string;
    waitingMinutes?: number;
};

export type OperationsDay = {
    companyId: number;
    companyIdApi: number | null;
    date: string | null;
    timezone: string | null;
    generatedAt: string;
    revenue: number | null;
    previousRevenue: number | null;
    comparisonDate?: string;
    dayPlan: number | null;
    riskAmount: number | null;
    history: { from: string; to: string; serviceRevenue: number; scheduledMinutes: number; rubPerMinute: number | null } | null;
    permissions: OperationsPermissions;
    situations: Operation[];
    sources: Record<string, "ok" | "forbidden" | "unavailable" | "not_connected">;
    cache?: { ready: boolean; refreshing: boolean; refreshError: boolean; refreshInterval: number };
};

export const sourceLabels: Record<string, string> = {
    revenue: "Выручка", schedule: "Расписание", cancellations: "Отмены", support: "Поддержка", plan: "План",
};

export function operationsStatus(data: OperationsDay | null, loading: boolean, error: string | null): "checking" | "unavailable" | "partial" | "ready" {
    if (error || data?.cache?.refreshError) return "unavailable";
    if (loading || data?.cache?.refreshing || data?.cache?.ready === false) return "checking";
    if (!data) return "unavailable";
    // An empty or incomplete sources map cannot confirm the absence of issues.
    return Object.keys(sourceLabels).every(source => data.sources?.[source] === "ok") ? "ready" : "partial";
}

export function operationTitle(item: Operation): string {
    switch (item.type) {
        case "gap": return "Свободное окно";
        case "shift": return "Смена не открыта";
        case "cancellation": return "Отмена без перезаписи";
        case "support": return "Поддержка ждёт ответа";
    }
}

// API timestamps carry the salon offset. Avoid converting form values to the browser timezone.
export function localParts(value?: string | null): { date: string; time: string } {
    return { date: value?.slice(0, 10) || "", time: value?.slice(11, 16) || "" };
}

export function matchesGap(candidate: Cancellation, gap: Operation): boolean {
    return candidate.services.length > 0
        && candidate.services.every(service => gap.serviceIds?.includes(service.id))
        && candidate.services.reduce((sum, service) => sum + service.minutes, 0) <= (gap.minutes || 0);
}

export function bookingUrl(companyId: string, gap?: Operation, candidate?: Cancellation): string {
    const params = new URLSearchParams({ from: "operations-day", operationCompanyId: companyId });
    if (gap?.staffId && gap.start) {
        params.set("staffId", String(gap.staffId));
        params.set("date", localParts(gap.start).date);
        params.set("time", localParts(gap.start).time);
    }
    if (candidate) { params.set("cancellationId", String(candidate.recordId)); }
    return `/records/new?${params.toString()}`;
}
